<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <information v-if="isInformationModalVisible"></information>
      <spinner v-if="loading || initializingState"></spinner>

      <div class="content-heading">
        Upcoming transactions
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <dismissable-alert
              :isShowing="isShowingReinvestmentNotice"
              title="Please take note!"
              subtitle="Transactions reinvested at the same rate/term on the same day will be reinvested together into one Note."
              notice=" Transactions that do not amount to $20 or greater when combined will be repaid to the default destination."
              alertType="warning"
              :showIcon="true"
              icon="calendar-payment"
              :dismissible="false"
            />

            <!-- Scheduled Additional Investments Section -->
            <div v-if="isExtraReinvestmentsEnabled && scheduledAdditionalInvestments.length > 0" class="scheduled-investments-section">
              <h4 class="section-title">
                <em class="fa fa-plus-circle text-success"></em> Scheduled Additional Investments
              </h4>
              <p class="section-description">
                These additional deposits are scheduled to be combined with your reinvestments.
              </p>
              <div class="scheduled-investments-list">
                <div
                  v-for="investment in scheduledAdditionalInvestments"
                  :key="investment.id"
                  class="scheduled-investment-item"
                >
                  <div class="investment-info">
                    <span class="investment-notes">
                      <strong>{{ investment.noteId }}</strong>
                    </span>
                    <span class="investment-amount">
                      {{ formatAmount(Number(investment.amount)) }}
                    </span>
                    <span class="investment-date">
                      Reinvests on {{ formatDate(investment.reinvestDate) }}
                    </span>
                    <span class="investment-status">
                      <span class="label label-warning" v-if="investment.status === 'PENDING'">Pending</span>
                      <span class="label label-info" v-if="investment.status === 'FUNDS_TRANSFERRED'">Funds Transferred</span>
                      <span class="label label-success" v-if="investment.status === 'PROCESSED'">Processed</span>
                      <span class="label label-danger" v-if="investment.status === 'CANCELLED'">Cancelled</span>
                      <span class="label label-danger" v-if="investment.status === 'FAILED'">Failed</span>
                    </span>
                  </div>
                  <div class="investment-actions" v-if="investment.status === 'FUNDS_TRANSFERRED'">
                    <span class="text-muted"><em class="fa fa-clock-o"></em> Transfer in progress</span>
                  </div>
                  <div class="investment-actions" v-if="investment.status === 'PENDING'">
                    <button
                      type="button"
                      class="btn btn-default btn-sm"
                      @click="editAdditionalInvestment(investment)"
                    >
                      <em class="fa fa-pencil"></em> Edit
                    </button>
                    <button
                      type="button"
                      class="btn btn-danger btn-sm"
                      @click="confirmCancelAdditionalInvestment(investment)"
                    >
                      <em class="fa fa-times"></em> Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isExtraReinvestmentsEnabled && hasUpcomingTransactions" class="add-funds-section">
              <div class="row">
                <div class="col-md-12">
                  <div class="add-funds-explainer">
                    <p class="explainer-text">
                      <strong>Add funds to your reinvestments:</strong> Maturing notes or interest payments that are scheduled to reinvest can receive additional funds to reinvest alongside them. This allows you to increase your investment amount when your existing notes mature or have interest transact. You can add funds between 45 and 3 days before the reinvestment date.
                    </p>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-12 text-center">
                  <button
                    type="button"
                    class="btn btn-default btn-lg"
                    @click="handleAddFundsToReinvestments"
                  >
                    Add funds to my reinvestments
                  </button>
                </div>
              </div>
            </div>

            <upcoming-transactions
              :notes-transactions="notesTransactionsWithNoAction"
              :allowed-actions="actionsAllowedForThisTransaction"
              :funding-sources="fundingSources"
              :submits-disabled="submitsDisabled"
              :year-rates="yearRates"
              :action-provided="false"
              @actionChanged="actionChanged"
              @submit="submitUpcomingTransaction"
            >
              <em class="fa fa-exchange text-blue"></em>Transactions you need to
              provide direction on
            </upcoming-transactions>

            <upcoming-transactions
              :notes-transactions="notesTransactionsWithAction"
              :allowed-actions="actionsAllowedForThisTransaction"
              :funding-sources="fundingSources"
              :submits-disabled="submitsDisabled"
              :year-rates="yearRates"
              action-provided
              @actionChanged="actionChanged"
              @submit="submitUpcomingTransaction"
            >
              <em class="fa fa-exchange text-gray"></em>Transactions you've
              provided direction on
            </upcoming-transactions>
          </div>
        </div>
      </div>

      <add-money-modal
        v-if="showAddMoneyModal"
        :funding-sources="availableFundingSources"
        :available-notes="availableNotesForReinvestment"
        :loading="addMoneyLoading"
        :editing-investment="editingInvestment"
        @close="closeAddMoneyModal"
        @submit="submitAddMoney"
      />

      <!-- Cancel Confirmation Modal -->
      <transition name="modal" v-if="showCancelConfirmModal">
        <div class="modal-overlay" style="opacity: 1.09; display: block;">
          <div class="modal-wrapper showSweetAlert visible" data-custom-class="" data-has-cancel-button="true" data-has-confirm-button="true" data-allow-outside-click="false" data-has-done-function="true" data-animation="pop" data-timer="null" style="display: block; margin-top: -100px;">
            <h2 class="modal-title">Cancel Additional Investment</h2>
            <p class="modal-paragraph">
              Are you sure you want to cancel this additional investment of <strong>{{ cancellingInvestment ? formatAmount(Number(cancellingInvestment.amount)) : '' }}</strong> for note <strong>{{ cancellingInvestment ? cancellingInvestment.noteId : '' }}</strong>?
            </p>
            <p class="modal-paragraph text-muted">
              This action cannot be undone.
            </p>
            <div class="modal-buttons">
              <div class="row">
                <div class="col-md-12 text-center">
                  <button
                    type="button"
                    @click="closeCancelConfirmModal"
                    class="btn btn-info btn-lg"
                    :disabled="cancellingLoading"
                  >
                    Keep Investment
                  </button>
                  <button
                    type="button"
                    @click="cancelAdditionalInvestment"
                    class="btn btn-danger btn-lg"
                    :disabled="cancellingLoading"
                  >
                    {{ cancellingLoading ? 'Cancelling...' : 'Yes, Cancel It' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </section>
</template>
<script>
  import {
    mapActions,
    mapGetters,
    mapMutations,
    mapState,
  } from 'vuex';

  import { find, filter } from 'lodash';

  import {
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
    SHOW_INFORMATION_MODAL,
  } from '@/store/mutation-types';

  import Errors from '@/components/modals/Errors';
  import Information from '@/components/modals/Information';
  import spinner from '@/components/widgets/Spinner';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import UpcomingTransactions from '@/components/UpcomingTransactions/index';
  import AddMoneyModal from '@/components/modals/AddMoney';

  import {
    formatDate,
    formatAmount,
    formatRate,
    formatTerm,
    isStateRestricted,
    convertErrorStringsToArrayStrings,
    findRates,
    setActionTakenUpcomingTransactionHelper,
    setFundingSourceUpcomingTransactionHelper,
    orderUpcomingTransactions,
  } from '@/util';

  import Actions from '@/data/static/upcomingTransactionActions'; // eslint-disable-line no-unused-vars
  import TransactionTypes from '@/data/static/transactionTypes'; // eslint-disable-line no-unused-vars
  import TransactionStatuses from '@/data/static/transactionStatuses'; // eslint-disable-line no-unused-vars

  import {
    Constants,
  } from '../data/static/constants';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatRate,
      formatTerm,
      isStateRestricted,
      findRates,
    },
  };

  export default {
    name: 'upcomingTransactionsForInvestor',
    async beforeMount() {
      await this.checkUserStatus();
      this.setActiveAccountIndex();
      if (this.isReadOnly) {
        this.submitsDisabled = true;
      }
      await this.initializeState();
      this.loadInvestmentsValidations();
    },
    mixins: [mix],
    components: {
      errors: Errors,
      information: Information,
      'dismissable-alert': DismissableAlert,
      'upcoming-transactions': UpcomingTransactions,
      'add-money-modal': AddMoneyModal,
      spinner,
    },
    computed: {
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('transaction', [
        'loading',
      ]),
      ...mapState('app', [
        'fundingSourceTypes',
        'investmentsRestrictions',
        'user',
        'activeAccountId',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
      isReadOnly() {
        return this.user && this.user.userType === 'READ_ONLY';
      },
      isExtraReinvestmentsEnabled() {
        return true;
      },
      isShowingReinvestmentNotice() {
        return (this.notesTransactionsWithAction !== null
          && this.notesTransactionsWithAction.length > 0)
          || (this.notesTransactionsWithNoAction !== null
          && this.notesTransactionsWithNoAction.length > 0);
      },
      actionsAllowedForThisTransaction() {
        if (this.isReadOnly) {
          return [];
        }
        return this.notesTransactionsWithNoAction.concat(this.notesTransactionsWithAction)
          .reduce((acc, val) => acc + val.transaction.amount, 0) >= this.minimumInvestmentAmount
          ? [Actions.reinvest, Actions.donate, Actions.cashout]
          : [Actions.donate, Actions.cashout];
      },
      availableFundingSources() {
        return this.fundingSources.filter(fs =>
          !fs.removed && fs.type === this.fundingSourceTypes.BANK.name,
        );
      },
      hasUpcomingTransactions() {
        return (this.notesTransactionsWithNoAction &&
               this.notesTransactionsWithNoAction.length > 0) ||
               (this.notesTransactionsWithAction && this.notesTransactionsWithAction.length > 0);
      },
      availableNotesForReinvestment() {
        const allTransactions = [
          ...(this.notesTransactionsWithNoAction || []),
          ...(this.notesTransactionsWithAction || []),
        ];

        const noteMap = {};
        allTransactions.forEach((nt) => {
          const { maturityDate } = nt.transaction;
          const noteId = nt.transaction.note.noteId;
          if (!maturityDate || !noteId) return;
          if (!this.isWithinAllowedWindow(maturityDate)) return;

          if (!noteMap[noteId]) {
            noteMap[noteId] = {
              noteId,
              maturityDate,
            };
          }
        });

        return Object.values(noteMap);
      },
    },
    data() {
      return {
        actions: Actions,
        yearRates: [],
        fundingSources: [],
        dwollaTermsUrl: Constants.purchaseNote.dwollaTermsUrl,
        prospectusTermUrl: Constants.purchaseNote.prospectusTermUrl,
        notesTransactionsWithNoAction: [],
        notesTransactionsWithAction: [],
        minimumInvestmentAmount: null,
        submitsDisabled: false,
        initializingState: false,
        activeAccountIndex: -1,
        showAddMoneyModal: false,
        addMoneyLoading: false,
        scheduledAdditionalInvestments: [],
        editingInvestment: null,
        showCancelConfirmModal: false,
        cancellingInvestment: null,
        cancellingLoading: false,
      };
    },
    methods: {
      getYearRatesForNote({ shouldDisplayZeroRate, noteInterestTerm, noteInterestRate }) {
        const availableRateAndTerms = [...this.yearRates];
        if (shouldDisplayZeroRate) {
          availableRateAndTerms.unshift({
            id: 'custom-id',
            label: `0.00%, ${noteInterestTerm / 12} year${noteInterestTerm > 12 ? 's' : ''}`,
            months: noteInterestTerm,
            active: true,
            rate: noteInterestRate,
          });
        }
        return availableRateAndTerms;
      },
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      ...mapActions('account', [
        'getAccountFundingSources',
        'getAccountTransactions',
        'getAccountNotes',
        'getAccountExtraReinvestments',
        'createAccountExtraReinvestment',
        'cancelAccountExtraReinvestment',
      ]),
      ...mapActions('note', [
        'getInterestYearRates',
      ]),
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('transaction', [
        'updateUpcomingTransaction',
      ]),
      setActiveAccountIndex() {
        const activeAccount = this.user.accounts.find(ac => ac.id === this.activeAccountId);
        this.activeAccountIndex = this.user.accounts.indexOf(activeAccount);
      },
      checkUserStatus() {
        this.initializingState = true;
        if (!this.hasBeenCreatedAccount) {
          this.$router.push({ name: 'AccountSummary' });
          this.initializingState = false;
          return true;
        }

        return this.getUserStatus({
          accountId: this.activeAccountId,
        }).then(({ status, needs, hasFundingSource }) => {
          if (status === 'suspended') {
            this.$router.push({ name: 'AccountSuspended' });
          } else if (status === 'retry') {
            this.$router.push({ name: 'AccountCreation' });
          } else if (status === 'document') {
            this.$router.push({ name: 'DocumentUpload' });
          } else if (needs.includes('FUNDING_SOURCE') && !hasFundingSource) {
            this.$router.push({ name: 'AddFundingSource' });
          } else if (needs.includes('NOTE')) {
            this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
          }
          this.initializingState = false;
          return true;
        });
      },
      async initializeState() {
        try {
          this.initializingState = true;

          const fundingSources = await this.getAccountFundingSources(this.activeAccountId);

          this.fundingSources = fundingSources.filter(fss => fss.status === 'verified' &&
            (fss.type === this.fundingSourceTypes.BANK.name ||
              fss.type === this.fundingSourceTypes.CHECK.name ||
              fss.type === this.fundingSourceTypes.WIRE_TRANSFER.name));

          this.yearRates = this.investmentsRestrictions.ratesAndTerms;

          const accountTransactions = await this.getAccountTransactions(
            {
              accountId: this.activeAccountId,
              params: {
                transactionTypes: [
                  TransactionTypes.SELL_REINVEST,
                  TransactionTypes.SELL_DONATE,
                  TransactionTypes.SELL_MATURE,
                  TransactionTypes.INT_REINVEST,
                  TransactionTypes.INT_PAID,
                  TransactionTypes.INT_DONATE,
                ],
                statuses: TransactionStatuses.PREPENDING,
              },
            });

          this.buildNotesTransactionsObjects(
            accountTransactions,
          );

          await this.loadExtraReinvestments();

          this.initializingState = false;
        } catch (error) {
          this.setErrorsModalInfo(['Something went wrong. Please try reloading the website.']);
          this.showErrorsModal();
          this.initializingState = false;
        }
      },
      async loadExtraReinvestments() {
        try {
          const response = await this.getAccountExtraReinvestments(this.activeAccountId);
          this.scheduledAdditionalInvestments = response || [];
        } catch (error) {
          this.scheduledAdditionalInvestments = [];
        }
      },
      loadInvestmentsValidations() {
        this.minimumInvestmentAmount = this.investmentsRestrictions.minimumAmount;
      },
      buildNotesTransactionsObjects(transactions) {
        const {
        transactionsWithoutActionTaken,
        transactionsWithActionTaken,
      } = transactions.reduce((acc, t) => {
        const { term, actionTaken, type, amount, fundingSourceId, rate } = t;
        let interestYearRatesSelectedPreviously = null;

        const action = setActionTakenUpcomingTransactionHelper(actionTaken, type);
        const fundingSource =
          setFundingSourceUpcomingTransactionHelper(
            action,
            fundingSourceId,
            this.user.accounts[this.activeAccountIndex].defaultFundingSourceId,
            this.fundingSources,
          );

        if (!actionTaken) {
          let newRate = rate;
          let newTerm = term;
          if (action.name === Actions.reinvest.name) {
            const reinvestmentOption = this.handleReinvestment({ transaction: t });
            interestYearRatesSelectedPreviously = reinvestmentOption.interestRateAndTerm;
            newTerm = reinvestmentOption.term;
            newRate = reinvestmentOption.rate;
          }

          acc.transactionsWithoutActionTaken.push({
            interestRateAndTerm: interestYearRatesSelectedPreviously,
            transaction: t,
            termsOfConditionAcceptance: false,
            amountForDonation: formatAmount(amount),
            rate: newRate,
            term: newTerm,
            fundingSource,
            action,
          });
          return acc;
        }
        acc.transactionsWithActionTaken.push({
          interestRateAndTerm: null,
          transaction: t,
          termsOfConditionAcceptance: false,
          amountForDonation: formatAmount(amount),
          rate,
          term,
          fundingSource,
          action,
        });
        return acc;
      }, {
        transactionsWithActionTaken: [],
        transactionsWithoutActionTaken: [],
      });

        this.notesTransactionsWithNoAction =
            orderUpcomingTransactions(transactionsWithoutActionTaken);
        this.notesTransactionsWithAction =
            orderUpcomingTransactions(transactionsWithActionTaken);

        return {
          transactionsWithActionTaken: this.notesTransactionsWithAction,
          transactionsWithoutActionTaken: this.notesTransactionsWithNoAction,
        };
      },
      getZeroPctCustomRateAndTerm(term) {
        return {
          id: 'zero-pct-id',
          label: `0.00%, ${term / 12} year${term > 12 ? 's' : ''}`,
          rate: 0,
          term,
        };
      },
      handleReinvestment(upcomingTransaction, previousActionTaken = false) {
        const { note, rate, term, actionTaken } = upcomingTransaction.transaction;
        const interestTerm = term !== 0 ? term : note.interestTerm;
        const interestRate = rate !== 0 ? rate : note.interestRate;

        let interestRateAndTerm;
        let newRate;
        let newTerm;

        const yearRatesForNote = this.getYearRatesForNote(upcomingTransaction);
        const filteredYearRates = filter(
              this.yearRates,
              r => r.rate === interestRate && r.term === interestTerm,
            );
        const needsCustom =
        filteredYearRates.length === 0 && note.interestRate === 0 && note.interestTerm > 1;

        if (!needsCustom) {
          interestRateAndTerm = findRates(this.yearRates, interestTerm, interestRate);
          newRate = interestRateAndTerm.rate;
          newTerm = interestRateAndTerm.term;
          return {
            interestRateAndTerm,
            rate: newRate,
            term: newTerm,
          };
        }

        if (!term && !rate && previousActionTaken) {
          const lowerRates = filter(
                  yearRatesForNote,
                  item => item.term <= interestTerm,
                );
          interestRateAndTerm = lowerRates[0] || this.yearRates[0];
          newTerm = interestRateAndTerm.term;
          newRate = interestRateAndTerm.rate;
          return {
            interestRateAndTerm,
            rate: newRate,
            term: newTerm,
          };
        }

        newRate = !actionTaken ? note.interestRate : rate;
        newTerm = !actionTaken ? note.interestTerm : term;
        interestRateAndTerm = this.getZeroPctCustomRateAndTerm(newTerm);

        return {
          interestRateAndTerm,
          rate: newRate,
          term: newTerm,
        };
      },
      actionChanged({ action, index, actionPreviousTaken }) {
        const upcomingTransaction = actionPreviousTaken ? this.notesTransactionsWithAction[index]
          : this.notesTransactionsWithNoAction[index];

        upcomingTransaction.fundingSource = null;
        upcomingTransaction.interestRateAndTerm = null;

        if (action.name === Actions.cashout.name) {
          upcomingTransaction.fundingSource =
            find(this.fundingSources, fs => fs.id ===
            this.user.accounts[this.activeAccountIndex].defaultFundingSourceId);
        } else if (action.name === Actions.reinvest.name) {
          const reinvestOption = this.handleReinvestment(upcomingTransaction, actionPreviousTaken);
          upcomingTransaction.interestRateAndTerm = reinvestOption.interestRateAndTerm;
          upcomingTransaction.rate = reinvestOption.rate;
          upcomingTransaction.term = reinvestOption.term;
        }
      },
      submitUpcomingTransaction(noteTransaction, selectedRateAndTerm) {
        if (this.isReadOnly) {
          return;
        }
        this.submitsDisabled = true;

        const {
          transaction,
          fundingSource,
          action,
          termsOfConditionAcceptance,
        } = noteTransaction;

        const upcomingTransactionToUpdate = {
          actionTaken: action.name,
          fundingSourceId: fundingSource ? String(fundingSource.id) : '',
          rate: (selectedRateAndTerm ? selectedRateAndTerm.rate : -1),
          term: (selectedRateAndTerm ? selectedRateAndTerm.term : -1),
          termsOfConditionAcceptance,
        };

        this.updateUpcomingTransaction({
          payload: upcomingTransactionToUpdate,
          transactionId: transaction.id,
        })
        .then(async () => {
          await this.initializeState();
          this.submitsDisabled = false;
        })
        .catch((errors) => {
          this.setErrorsModalInfo(convertErrorStringsToArrayStrings(errors.message));
          this.showErrorsModal();
          this.submitsDisabled = false;
        });
      },
      isWithinAllowedWindow(maturityDate) {
        if (!maturityDate) return false;
        const maturity = new Date(maturityDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        maturity.setHours(0, 0, 0, 0);
        const diffTime = maturity - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 4 && diffDays <= 45;
      },
      handleAddFundsToReinvestments() {
        if (this.isReadOnly) {
          return;
        }
        this.editingInvestment = null;
        this.showAddMoneyModal = true;
      },
      closeAddMoneyModal() {
        this.showAddMoneyModal = false;
        this.editingInvestment = null;
      },
      async submitAddMoney({
        noteId,
        fundingSourceId,
        fundingSourceType,
        fundingSourceName,
        amount,
      }) {
        this.addMoneyLoading = true;
        try {
          if (this.editingInvestment) {
            await this.cancelAccountExtraReinvestment({
              accountId: this.activeAccountId,
              extraReinvestmentId: this.editingInvestment.id,
            });
          }

          await this.createAccountExtraReinvestment({
            accountId: this.activeAccountId,
            payload: { amount, noteId, fundingSourceId, fundingSourceType, fundingSourceName },
          });

          await this.loadExtraReinvestments();

          if (this.editingInvestment) {
            this.showInformationModal('Your additional investment has been updated successfully.');
          } else {
            this.showInformationModal(`Your additional investment of ${this.formatAmount(amount)} has been scheduled successfully.`);
          }

          this.closeAddMoneyModal();
        } catch (error) {
          this.setErrorsModalInfo(convertErrorStringsToArrayStrings(error.message || 'Failed to process your request. Please try again.'));
          this.showErrorsModal();
        } finally {
          this.addMoneyLoading = false;
        }
      },
      editAdditionalInvestment(investment) {
        this.editingInvestment = { ...investment };
        this.showAddMoneyModal = true;
      },
      confirmCancelAdditionalInvestment(investment) {
        this.cancellingInvestment = investment;
        this.showCancelConfirmModal = true;
      },
      closeCancelConfirmModal() {
        this.showCancelConfirmModal = false;
        this.cancellingInvestment = null;
      },
      async cancelAdditionalInvestment() {
        if (!this.cancellingInvestment) return;
        this.cancellingLoading = true;
        try {
          await this.cancelAccountExtraReinvestment({
            accountId: this.activeAccountId,
            extraReinvestmentId: this.cancellingInvestment.id,
          });
          await this.loadExtraReinvestments();
          this.showInformationModal('Your additional investment has been cancelled.');
          this.closeCancelConfirmModal();
        } catch (error) {
          this.setErrorsModalInfo(convertErrorStringsToArrayStrings(error.message || 'Failed to cancel investment. Please try again.'));
          this.showErrorsModal();
        } finally {
          this.cancellingLoading = false;
        }
      },
    },
  };
</script>

<style scoped>
  .content-heading {
    font-weight: normal;
  }
  .panel-default {
    padding: 20px 30px;
  }
  select, input {
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 40px;
  }
  .text-blue {
    color: #23b7e5;
  }
  .panel-title .fa {
    margin-right: 10px;
  }
  .panel .panel .panel-title {
    margin-bottom: 15px;
    font-size: 16px;
  }
  .add-funds-section {
    margin: 20px 0;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 4px;
  }
  .add-funds-explainer {
    margin-bottom: 15px;
  }
  .explainer-text {
    font-size: 14px;
    line-height: 1.6;
    color: #555;
    margin-bottom: 0;
  }
  .top-margin {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #eee;
  }
  /* Scheduled Additional Investments Section */
  .scheduled-investments-section {
    margin: 20px 0;
    padding: 20px;
    background-color: #f0f9f0;
    border: 1px solid #c3e6c3;
    border-radius: 4px;
  }
  .scheduled-investments-section .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
  }
  .scheduled-investments-section .section-title .fa {
    margin-right: 8px;
    color: #5cb85c;
  }
  .section-description {
    font-size: 14px;
    color: #666;
    margin-bottom: 15px;
  }
  .scheduled-investments-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .scheduled-investment-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  .investment-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .investment-notes {
    font-size: 16px;
    color: #333;
  }
  .investment-amount {
    font-size: 18px;
    font-weight: 600;
    color: #127285;
  }
  .investment-date {
    font-size: 13px;
    color: #888;
  }
  .investment-status {
    font-size: 12px;
    color: #666;
    text-transform: capitalize;
  }
  .investment-actions {
    display: flex;
    gap: 8px;
  }
  .investment-actions .btn {
    min-width: 80px;
  }
  .investment-actions .btn .fa {
    margin-right: 4px;
  }
  /* Cancel Confirmation Modal */
  .modal-overlay {
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .modal-wrapper {
    background-color: #fff;
    padding: 30px;
    border-radius: 8px;
    max-width: 480px;
    width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  .modal-title {
    color: #127285;
    text-align: center;
    margin-bottom: 15px;
  }
  .modal-paragraph {
    font-size: 16px;
    text-align: center;
    margin-bottom: 10px;
  }
  .modal-buttons {
    margin-top: 20px;
  }
  .modal-buttons .btn {
    min-width: 140px;
    margin: 0 5px;
  }
</style>



// WEBPACK FOOTER //
// UpcomingTransactionsForInvestor.vue?f3f9c3ea